// ── Pay an online business ────────────────────────────────────────
//
// Closes the discovery → payment loop for ONLINE businesses (the case
// BCHPay.cash's in-person POS doesn't cover). The customer enters an
// arbitrary amount and pays either:
//
//   1. from their in-app BCHNostr wallet (one tap, sendTipWithFee), or
//   2. from ANY external wallet via a BIP21 QR / deep link.
//
// Deliberately NOT verified on-chain: like the app's tip receipts, the
// published receipt is social proof of a payment, never settlement. The
// merchant reconciles in their own wallet. See NIP.md § kind 30499.

import React, { useState, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import {
  Coins, X, Check, Loader2, Wallet, QrCode, Copy, ExternalLink, AlertCircle,
} from 'lucide-react';
import { useAppContext } from '../hooks/useAppContext';
import { useTipWallet } from '../hooks/useTipWallet';
import { useNostrPublish } from '../hooks/useNostrPublish';
import { getBCHPrice } from '../hooks/useBCHPrice';
import { feeBreakdown } from '../lib/platform';

/** Receipt kind — same as tips, distinguished by the `bch-payment` tag. */
const PAYMENT_RECEIPT_KIND = 9735;

const PRESET_USD = [1, 5, 10, 25, 50];

type PayState = 'idle' | 'sending' | 'sent' | 'error' | 'no-funds';
type PayTab = 'wallet' | 'qr';

interface PayBusinessButtonProps {
  /** Business pubkey (receipt is p-tagged to them). */
  recipientPubkey: string;
  /** Business CashAddr, from the kind 30499 `bch` tag. */
  address: string;
  /** Business display name, for labels and the BIP21 label param. */
  businessName?: string;
  /** Render a full-width button instead of the compact inline one. */
  fullWidth?: boolean;
}

/** Normalise to a bare cashaddr (no scheme) for display/copy. */
function bareAddress(addr: string): string {
  return addr.replace(/^bitcoincash:/i, '');
}

/** Build a BIP21 URI so any BCH wallet can pay the exact amount. */
function bip21Uri(address: string, bchAmount: number, label?: string): string {
  const addr = bareAddress(address);
  const params = new URLSearchParams();
  if (bchAmount > 0) params.set('amount', bchAmount.toFixed(8).replace(/0+$/, '').replace(/\.$/, ''));
  if (label) params.set('label', label);
  const qs = params.toString();
  return `bitcoincash:${addr}${qs ? `?${qs}` : ''}`;
}

export default function PayBusinessButton({
  recipientPubkey, address, businessName, fullWidth = false,
}: PayBusinessButtonProps) {
  const { user } = useAppContext();
  const tipWallet = useTipWallet();
  const { publish } = useNostrPublish();

  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<PayTab>('wallet');
  const [state, setState] = useState<PayState>('idle');
  const [usdInput, setUsdInput] = useState('');
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [txid, setTxid] = useState('');

  const isSelf = user?.pubkey === recipientPubkey;
  const price = getBCHPrice();

  const usd = parseFloat(usdInput) || 0;
  const bchAmount = price && usd > 0 ? usd / price : 0;
  const sats = bchAmount > 0 ? BigInt(Math.round(bchAmount * 1e8)) : BigInt(0);

  const fees = useMemo(
    () => (sats > BigInt(0) ? feeBreakdown(Number(sats)) : null),
    [sats],
  );

  const uri = useMemo(
    () => bip21Uri(address, bchAmount, businessName),
    [address, bchAmount, businessName],
  );

  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=240x240&margin=10&format=png&ecc=M&data=${encodeURIComponent(uri)}`;

  const openSheet = useCallback(() => {
    setOpen(true);
    setState('idle');
    setError('');
    // Warm the wallet so the first payment isn't slowed by init.
    tipWallet.init().catch(() => {});
  }, [tipWallet]);

  const close = useCallback(() => {
    setOpen(false);
    setState('idle');
    setError('');
    setTxid('');
  }, []);

  // ── Pay from the in-app wallet ──
  const handleWalletPay = useCallback(async () => {
    setError('');

    if (!price) {
      setError('BCH price unavailable — try again in a moment.');
      setState('error');
      return;
    }
    if (sats <= BigInt(0)) {
      setError('Enter an amount first.');
      setState('error');
      return;
    }
    // Leave headroom for the miner fee.
    if (tipWallet.balanceSats < sats + BigInt(300)) {
      setState('no-funds');
      return;
    }

    setState('sending');
    try {
      if (!tipWallet.ready) await tipWallet.init();
      const id = await tipWallet.sendTipWithFee(address, sats);
      setTxid(id);
      setState('sent');

      // Social-proof receipt (fire-and-forget, never settlement).
      publish({
        kind: PAYMENT_RECEIPT_KIND,
        content: JSON.stringify({
          amount_sats: Number(sats),
          amount_usd: usd,
          txid: id || '',
          message: businessName ? `Payment to ${businessName}` : 'Payment',
        }),
        tags: [
          ['p', recipientPubkey],
          ['amount', Number(sats).toString()],
          ['t', 'bch-payment'],
        ],
        created_at: Math.floor(Date.now() / 1000),
      }).catch(() => {});
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      console.error('[PayBusiness] send failed:', msg);
      setError(
        /cashaddr|decode|invalid.*address|checksum/i.test(msg)
          ? "This business's BCH address looks invalid."
          : /network|timeout|fetch|electrum/i.test(msg)
            ? 'Network error — please try again.'
            : msg || 'Payment failed.',
      );
      setState('error');
    }
  }, [price, sats, usd, tipWallet, address, publish, recipientPubkey, businessName]);

  const copyUri = useCallback(() => {
    navigator.clipboard.writeText(bareAddress(address)).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(() => {});
  }, [address]);

  if (!address || isSelf) return null;

  const sheet = open ? createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-0 sm:p-4"
      onClick={close}
    >
      <div
        className="w-full sm:max-w-sm bg-card border border-border rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[90dvh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <h3 className="text-sm font-bold flex items-center gap-2">
            <Coins className="w-4 h-4 text-primary" />
            Pay {businessName || 'business'}
          </h3>
          <button onClick={close} className="p-1 rounded-lg hover:bg-muted" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>

        {state === 'sent' ? (
          /* ── Success ── */
          <div className="p-6 text-center space-y-3">
            <div className="w-12 h-12 mx-auto rounded-full bg-primary/10 flex items-center justify-center">
              <Check className="w-6 h-6 text-primary" />
            </div>
            <div>
              <p className="font-bold text-sm">Payment sent</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                ${usd.toFixed(2)} to {businessName || 'the business'}
              </p>
            </div>
            {txid && (
              <a
                href={`https://blockchair.com/bitcoin-cash/transaction/${txid}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
              >
                View transaction <ExternalLink className="w-3 h-3" />
              </a>
            )}
            <p className="text-[10px] text-muted-foreground">
              The business confirms receipt in their own wallet.
            </p>
            <button
              onClick={close}
              className="w-full py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-semibold"
            >
              Done
            </button>
          </div>
        ) : (
          <div className="p-4 space-y-4">
            {/* Amount */}
            <div>
              <label className="text-[11px] font-medium text-muted-foreground">Amount (USD)</label>
              <div className="relative mt-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">$</span>
                <input
                  type="number"
                  inputMode="decimal"
                  min="0"
                  step="0.01"
                  value={usdInput}
                  onChange={e => { setUsdInput(e.target.value); setState('idle'); setError(''); }}
                  placeholder="0.00"
                  className="w-full bg-muted rounded-lg pl-7 pr-3 py-2.5 text-sm font-semibold focus:outline-none focus:ring-1 focus:ring-primary"
                />
              </div>
              <div className="flex gap-1.5 mt-2">
                {PRESET_USD.map(p => (
                  <button
                    key={p}
                    onClick={() => { setUsdInput(String(p)); setState('idle'); setError(''); }}
                    className={`flex-1 py-1 rounded-md text-[11px] font-medium transition-colors ${
                      usd === p ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    ${p}
                  </button>
                ))}
              </div>
              {bchAmount > 0 && (
                <p className="text-[10px] text-muted-foreground mt-1.5">
                  ≈ {bchAmount.toFixed(8).replace(/0+$/, '').replace(/\.$/, '')} BCH
                  {fees && fees.fee > 0 && fees.total > 0 && (
                    <> · business receives ~${(usd * (fees.net / fees.total)).toFixed(2)} after the 1% platform fee</>
                  )}
                </p>
              )}
              {!price && (
                <p className="text-[10px] text-amber-500 mt-1">BCH price loading…</p>
              )}
            </div>

            {/* Tabs */}
            <div className="flex gap-1 p-1 bg-muted rounded-lg">
              <button
                onClick={() => setTab('wallet')}
                className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-md text-xs font-medium transition-colors ${
                  tab === 'wallet' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground'
                }`}
              >
                <Wallet className="w-3.5 h-3.5" /> In-app wallet
              </button>
              <button
                onClick={() => setTab('qr')}
                className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-md text-xs font-medium transition-colors ${
                  tab === 'qr' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground'
                }`}
              >
                <QrCode className="w-3.5 h-3.5" /> Any wallet
              </button>
            </div>

            {tab === 'wallet' ? (
              /* ── Pay from in-app wallet ── */
              <div className="space-y-2">
                <div className="flex items-center justify-between text-[11px] text-muted-foreground">
                  <span>Your balance</span>
                  <span className="font-mono font-medium text-foreground">
                    {(Number(tipWallet.balanceSats) / 1e8).toFixed(8).replace(/0+$/, '').replace(/\.$/, '')} BCH
                  </span>
                </div>

                {state === 'no-funds' && (
                  <div className="flex items-start gap-2 rounded-lg bg-amber-500/10 border border-amber-500/20 px-3 py-2">
                    <AlertCircle className="w-3.5 h-3.5 text-amber-500 shrink-0 mt-0.5" />
                    <p className="text-[11px] text-amber-600 dark:text-amber-400">
                      Not enough in your in-app wallet. Top it up in Settings, or use
                      the <strong>Any wallet</strong> tab to pay from another wallet.
                    </p>
                  </div>
                )}
                {state === 'error' && error && (
                  <div className="flex items-start gap-2 rounded-lg bg-red-500/10 border border-red-500/20 px-3 py-2">
                    <AlertCircle className="w-3.5 h-3.5 text-red-500 shrink-0 mt-0.5" />
                    <p className="text-[11px] text-red-500">{error}</p>
                  </div>
                )}

                <button
                  onClick={handleWalletPay}
                  disabled={state === 'sending' || sats <= BigInt(0) || !user}
                  className="w-full py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {state === 'sending'
                    ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</>
                    : <><Coins className="w-4 h-4" /> Pay {usd > 0 ? `$${usd.toFixed(2)}` : ''}</>}
                </button>
                {!user && (
                  <p className="text-[10px] text-center text-muted-foreground">Log in to pay from the in-app wallet.</p>
                )}
              </div>
            ) : (
              /* ── Pay from any wallet (BIP21) ── */
              <div className="space-y-3">
                <div className="flex justify-center">
                  <img
                    src={qrUrl}
                    alt="Payment QR code"
                    width={200}
                    height={200}
                    className="rounded-lg bg-white p-2"
                  />
                </div>
                <p className="text-[10px] text-center text-muted-foreground">
                  {usd > 0
                    ? 'Scan with any BCH wallet — the amount is included.'
                    : 'Enter an amount above to include it in the QR code.'}
                </p>

                <button
                  onClick={copyUri}
                  className="w-full flex items-center justify-between gap-2 rounded-lg bg-muted px-3 py-2 text-left hover:bg-muted/80 transition-colors"
                >
                  <span className="font-mono text-[10px] truncate">{bareAddress(address)}</span>
                  {copied
                    ? <Check className="w-3.5 h-3.5 text-primary shrink-0" />
                    : <Copy className="w-3.5 h-3.5 text-muted-foreground shrink-0" />}
                </button>

                <a
                  href={uri}
                  className="w-full py-2.5 rounded-lg border border-primary text-primary text-sm font-semibold hover:bg-primary/10 flex items-center justify-center gap-2"
                >
                  <ExternalLink className="w-4 h-4" /> Open in wallet
                </a>
                <p className="text-[10px] text-center text-muted-foreground">
                  Paid externally? The business confirms it in their own wallet.
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>,
    document.body,
  ) : null;

  return (
    <>
      <button
        onClick={openSheet}
        className={`flex items-center justify-center gap-1.5 rounded-lg bg-primary text-primary-foreground font-semibold hover:bg-primary/90 transition-colors ${
          fullWidth ? 'w-full py-2.5 text-sm' : 'px-3 py-1.5 text-xs'
        }`}
      >
        <Coins className={fullWidth ? 'w-4 h-4' : 'w-3.5 h-3.5'} /> Pay
      </button>
      {sheet}
    </>
  );
}
